<script setup lang="ts">
/**
 * Pulsante flottante «Utilità» (in basso a destra).
 *
 * <h2>Perché esiste</h2>
 * Gli strumenti trasversali — quelli che servono <em>mentre</em> stai facendo altro, non quando
 * apri la loro pagina — non hanno un posto naturale: nel menu si perdono (nessuno naviga i
 * sottomenu per cercare un'utilità), nella topbar non c'è più spazio (otto icone) e un widget in
 * dashboard servirebbe proprio dove non serve, visto che il momento del bisogno arriva quando sei
 * su un'altra pagina.
 *
 * Il FAB risolve il problema una volta sola: sempre a portata da qualunque pagina, costo zero in
 * spazio schermo, e ogni utilità futura è una riga in {@link VOCI}.
 *
 * <h2>Scelte</h2>
 * Lista verticale con etichette e non menu radiale: le etichette si leggono, le icone da sole
 * andrebbero indovinate. Chiusura con Esc o click fuori.
 *
 * Non ci sono i preferiti: hanno già la stella in topbar, con il loro pannello. Duplicare la stessa
 * navigazione in due posti è il modo più rapido per rendere incoerente l'interfaccia.
 *
 * z-index sotto dialog e toast di proposito: quando c'è un dialog aperto, il FAB deve stare
 * dietro, non galleggiarci sopra.
 */
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useScreenshotStore } from '@/stores/screenshot'
import { useAuthStore } from '@/stores/auth'
import PromemoriaPanel from '@/components/common/PromemoriaPanel.vue'
import { useDaFare } from '@/composables/useDaFare'

interface VoceUtilita {
  chiave: string
  etichettaDefault: string
  icona: string
  /** Route da aprire; in alternativa `pannello` per aprire un contenuto qui dentro. */
  route?: string
  /** Apre un pannello invece di navigare: non fa perdere la pagina su cui si sta lavorando. */
  pannello?: 'promemoria'
  /** Permesso richiesto; assente = visibile a tutti. */
  permesso?: string
  /** Visibile solo a chi NON ha questo permesso (per le voci che hanno un'alternativa migliore). */
  nascondiSe?: string
  /** Aperta anche all'amministratore dell'azienda (come `oppureAmministratoreAzienda` delle route). */
  oppureAmministratore?: boolean
}

/**
 * Le utilità del FAB, in ordine di apparizione. Aggiungerne una = aggiungere una riga qui.
 *
 * Le voci sono filtrate per permesso: un operatore helpdesk non ha bisogno del pulsante per
 * *aprire* un ticket — gli serve la coda da lavorare; un super-admin ha due strumenti diagnostici
 * che dalla pagina in cui si trova non raggiungerebbe senza passare dal menu Amministrazione.
 */
const VOCI: VoceUtilita[] = [
  {
    chiave: 'utilita.promemoria',
    etichettaDefault: 'Promemoria',
    icona: 'pi pi-check-square',
    pannello: 'promemoria',
  },
  {
    chiave: 'utilita.strumentiPdf',
    etichettaDefault: 'Strumenti PDF',
    icona: 'pi pi-file-pdf',
    route: '/strumenti/pdf',
  },
  {
    // Come «Strumenti PDF»: serve mentre si lavora su altro (es. un documento da firmare).
    // Con una firma assegnata si firma; senza, la pagina spiega come ottenere il kit.
    chiave: 'utilita.firmaDigitale',
    etichettaDefault: 'Firma digitale',
    icona: 'pi pi-verified',
    route: '/strumenti/firma-digitale',
    permesso: 'FIRMA_REMOTA_USE',
  },
  {
    // Dati impresa e visure camerali: dopo «Firma digitale», come nel menu Assistenza. Il backend decide chi può usarli
    // (amministratore dell'azienda o OPENAPI_SERVIZI_USE); la pagina spiega cosa fare se mancano servizi o token.
    chiave: 'utilita.serviziOpenapi',
    etichettaDefault: 'Servizi OpenAPI',
    icona: 'pi pi-search',
    route: '/strumenti/servizi-openapi',
    permesso: 'OPENAPI_SERVIZI_USE',
    oppureAmministratore: true,
  },
  {
    // Azione, non navigazione: da qualunque pagina si apre una richiesta di assistenza senza
    // dover cercare il menu Assistenza. Per chi i ticket li lavora (TICKET_OPERATOR) è invece
    // la coda ad essere utile, e prende il suo posto.
    chiave: 'utilita.nuovoTicket',
    etichettaDefault: 'Richiedi assistenza',
    icona: 'pi pi-ticket',
    route: '/ticket/nuovo',
    nascondiSe: 'TICKET_OPERATOR',
  },
  {
    chiave: 'utilita.codaHelpdesk',
    etichettaDefault: 'Coda Helpdesk',
    icona: 'pi pi-inbox',
    route: '/helpdesk',
    permesso: 'TICKET_OPERATOR',
  },
  {
    chiave: 'utilita.diagnostica',
    etichettaDefault: 'Diagnostica',
    icona: 'pi pi-heart',
    route: '/admin/diagnostics',
    permesso: 'PLATFORM_SUPERADMIN',
  },
  {
    chiave: 'utilita.comunicazioniSistema',
    etichettaDefault: 'Comunicazioni di sistema',
    icona: 'pi pi-wifi',
    route: '/admin/sse-debug',
    permesso: 'PLATFORM_SUPERADMIN',
  },
]

const router = useRouter()
const { t } = useI18n()
const screenshot = useScreenshotStore()
const auth = useAuthStore()

const aperto = ref(false)
const pannelloPromemoria = ref(false)
const radice = ref<HTMLElement | null>(null)

// ── Campanella promemoria scaduti ────────────────────────────────────────
// Badge rosso sul FAB col numero di promemoria APERTI con scadenza passata (data, o data odierna
// con ora già trascorsa). Conteggio calcolato qui e non dal backend: sono le stesse righe che il
// pannello mostra, e una chiamata leggera all'apertura del portale basta — niente polling.
const daFare = useDaFare()
const scaduti = ref(0)

async function contaScaduti() {
  try {
    const aperti = await daFare.elenco(false)
    const adesso = new Date()
    const oggi = `${adesso.getFullYear()}-${String(adesso.getMonth() + 1).padStart(2, '0')}-${String(adesso.getDate()).padStart(2, '0')}`
    const hhmm = `${String(adesso.getHours()).padStart(2, '0')}:${String(adesso.getMinutes()).padStart(2, '0')}`
    scaduti.value = aperti.filter(p => {
      if (!p.scadenza) return false
      if (p.scadenza < oggi) return true
      if (p.scadenza > oggi) return false
      return !!p.scadenzaOra && p.scadenzaOra.slice(0, 5) < hhmm
    }).length
  } catch {
    scaduti.value = 0
  }
}

// Il pannello è il posto dove si spuntano/spostano le scadenze: alla chiusura si ricalcola.
watch(pannelloPromemoria, (apertoOra) => { if (!apertoOra) void contaScaduti() })

/**
 * Le voci visibili a chi sta guardando. `PLATFORM_SUPERADMIN` passa solo ai super-admin
 * (vedi `authStore.has`), quindi le voci diagnostiche non compaiono a nessun altro.
 */
const amministratoreAzienda = computed(() => auth.isPlatformSuperAdmin || !!auth.me?.amministratoreGenerale)
const vociVisibili = computed(() => VOCI.filter(v =>
  (!v.permesso || auth.has(v.permesso) || (v.oppureAmministratore === true && amministratoreAzienda.value))
  && (!v.nascondiSe || !auth.has(v.nascondiSe))
))

/**
 * In modalità cattura schermate il FAB sparisce: finirebbe in ogni screenshot e in ogni video
 * della guida, coprendo per giunta l'angolo in basso a destra della pagina catturata.
 */
const nascosto = computed(() => screenshot.attivo)

function apriChiudi() {
  aperto.value = !aperto.value
}

function scegli(voce: VoceUtilita) {
  aperto.value = false
  if (voce.pannello === 'promemoria') {
    // Pannello e non pagina: i promemoria si consultano mentre si sta facendo altro, e
    // navigare via farebbe perdere il form in compilazione.
    pannelloPromemoria.value = true
    return
  }
  if (voce.route) router.push(voce.route)
}

function suEsc(e: KeyboardEvent) {
  if (e.key === 'Escape') aperto.value = false
}

function suClickFuori(e: MouseEvent) {
  if (!aperto.value) return
  if (radice.value && !radice.value.contains(e.target as Node)) aperto.value = false
}

onMounted(() => {
  document.addEventListener('keydown', suEsc)
  document.addEventListener('click', suClickFuori)
  void contaScaduti()
})

onBeforeUnmount(() => {
  document.removeEventListener('keydown', suEsc)
  document.removeEventListener('click', suClickFuori)
})
</script>

<template>
  <div
    v-if="!nascosto"
    ref="radice"
    class="fab-utilita"
  >
    <!-- Velo leggero: le etichette stanno sopra il contenuto della pagina e senza uno stacco
         si leggono male su tabelle ed elenchi fitti. -->
    <transition name="fab-velo">
      <div
        v-if="aperto"
        class="fab-velo"
        @click="aperto = false"
      />
    </transition>

    <transition-group
      name="fab-voce"
      tag="div"
      class="fab-voci"
    >
      <button
        v-for="(v, i) in (aperto ? vociVisibili : [])"
        :key="v.chiave"
        type="button"
        class="fab-voce"
        :style="{ transitionDelay: `${i * 35}ms` }"
        @click="scegli(v)"
      >
        <span class="fab-voce-testo">
          {{ t(v.chiave, v.etichettaDefault) }}
          <span
            v-if="v.pannello === 'promemoria' && scaduti > 0"
            class="fab-voce-badge"
          >{{ scaduti }}</span>
        </span>
        <span class="fab-voce-icona">
          <i :class="v.icona" />
        </span>
      </button>
    </transition-group>

    <button
      type="button"
      class="fab-principale"
      :class="{ 'fab-principale--aperto': aperto }"
      :aria-expanded="aperto"
      :aria-label="t('utilita.titolo', 'Utilità')"
      :title="t('utilita.titolo', 'Utilità')"
      @click="apriChiudi"
    >
      <i :class="aperto ? 'pi pi-times' : 'pi pi-bars'" />
      <span
        v-if="scaduti > 0 && !aperto"
        class="fab-badge"
        :title="t('daFare.scadutiBadge', 'Promemoria scaduti')"
      >{{ scaduti > 9 ? '9+' : scaduti }}</span>
    </button>

    <Dialog
      v-model:visible="pannelloPromemoria"
      modal
      dismissable-mask
      :header="t('daFare.promemoria', 'Promemoria')"
      :style="{ width: '34rem', maxWidth: '95vw' }"
    >
      <PromemoriaPanel />
    </Dialog>
  </div>
</template>

<style scoped>
.fab-utilita {
  position: fixed;
  right: 1.25rem;
  bottom: 1.25rem;
  /* Sotto dialog (1100+) e toast: un pulsante flottante non deve mai coprire una finestra modale. */
  z-index: 900;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.5rem;
}

.fab-velo {
  position: fixed;
  inset: 0;
  background: rgb(0 0 0 / 18%);
  z-index: -1;
}

.fab-velo-enter-active,
.fab-velo-leave-active {
  transition: opacity 0.16s ease;
}
.fab-velo-enter-from,
.fab-velo-leave-to {
  opacity: 0;
}

.fab-voci {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.5rem;
}

.fab-principale {
  position: relative;
  /* Volutamente grande e con ombra marcata: è l'unico punto d'accesso agli strumenti
     trasversali, se si mimetizza col contenuto non lo trova nessuno. */
  width: 3.5rem;
  height: 3.5rem;
  border: none;
  border-radius: 50%;
  background: var(--p-primary-color);
  color: var(--p-primary-contrast-color);
  box-shadow: 0 6px 18px rgb(0 0 0 / 32%), 0 0 0 4px rgb(255 255 255 / 55%);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  transition: transform 0.18s ease, box-shadow 0.18s ease, background 0.18s ease;
}
.fab-principale:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 22px rgb(0 0 0 / 38%), 0 0 0 4px rgb(255 255 255 / 55%);
}
.fab-principale:focus-visible {
  outline: 2px solid var(--p-primary-color);
  outline-offset: 3px;
}
.fab-principale--aperto {
  transform: rotate(90deg);
}
.fab-principale--aperto:hover {
  transform: rotate(90deg) translateY(-2px);
}

.fab-voce {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
}

.fab-voce-testo {
  padding: 0.3rem 0.6rem;
  border-radius: 6px;
  background: var(--p-content-background);
  color: var(--p-text-color);
  border: 1px solid var(--p-content-border-color);
  box-shadow: 0 2px 8px rgb(0 0 0 / 12%);
  font-size: 0.8rem;
  white-space: nowrap;
}

.fab-voce-icona {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  background: var(--p-content-background);
  border: 1px solid var(--p-content-border-color);
  color: var(--p-primary-color);
  box-shadow: 0 2px 8px rgb(0 0 0 / 15%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.95rem;
  transition: background 0.15s, color 0.15s;
}
.fab-voce:hover .fab-voce-icona {
  background: var(--p-primary-color);
  color: var(--p-primary-contrast-color);
}

.fab-badge {
  position: absolute;
  top: -0.2rem;
  right: -0.2rem;
  min-width: 1.15rem;
  height: 1.15rem;
  padding: 0 0.25rem;
  border-radius: 999px;
  background: var(--p-red-500);
  color: #fff;
  font-size: 0.68rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 4px rgb(0 0 0 / 30%);
}

.fab-voce-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1rem;
  height: 1rem;
  padding: 0 0.2rem;
  margin-left: 0.3rem;
  border-radius: 999px;
  background: var(--p-red-500);
  color: #fff;
  font-size: 0.65rem;
  font-weight: 700;
}

/* Le voci entrano dal basso, sfalsate: dice da dove arrivano e dove torneranno. */
.fab-voce-enter-active,
.fab-voce-leave-active {
  transition: opacity 0.16s ease, transform 0.16s ease;
}
.fab-voce-enter-from,
.fab-voce-leave-to {
  opacity: 0;
  transform: translateY(0.75rem) scale(0.95);
}

@media print {
  .fab-utilita {
    display: none;
  }
}
</style>
